"use client";

// Lua-original primitive — NOT from the @ai-elements registry.
// Renders a list of choice cards inside a chat surface. Naming matches
// the AI Elements collection convention (Sources/Source,
// Attachments/Attachment, Suggestions/Suggestion → MessageCards/MessageCard).
//
// Why this exists: every chat surface across lua-admin / lua-pop /
// demo-website was reimplementing the same ~30 lines of responsive
// "carousel on desktop, stack on mobile" composition. Centralised here.

import {
  type AnchorHTMLAttributes,
  type ComponentProps,
  createContext,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useContext,
} from "react";
import { cn } from "@/lib/utils";

const cardChrome =
  "flex flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground";

export type MessageCardsLayout = "stack" | "carousel" | "responsive";

// Lets MessageCards propagate its layout to children without consumers
// having to thread it onto every MessageCard manually. The explicit
// `layout` prop on a card still wins, so a single MessageCard rendered
// outside a collection (or one that needs to differ) can override.
const MessageCardsLayoutContext = createContext<MessageCardsLayout | null>(
  null,
);

export type MessageCardsProps = ComponentProps<"div"> & {
  /**
   * `stack` — vertical column at every viewport.
   * `carousel` — horizontal scroll at every viewport.
   * `responsive` (default) — stack on mobile, carousel on md+.
   *
   * No JS viewport detection; the responsive switch is pure Tailwind
   * breakpoints so SSR + hydration are stable.
   */
  layout?: MessageCardsLayout;
};

const layoutClasses: Record<MessageCardsLayout, string> = {
  stack: "flex flex-col gap-2",
  carousel:
    "flex flex-row gap-3 overflow-x-auto snap-x snap-mandatory pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
  responsive:
    "flex flex-col gap-2 md:flex-row md:gap-3 md:overflow-x-auto md:snap-x md:snap-mandatory md:pb-2 md:[scrollbar-width:none] md:[&::-webkit-scrollbar]:hidden",
};

export const MessageCards = ({
  layout = "responsive",
  className,
  ...props
}: MessageCardsProps) => (
  <MessageCardsLayoutContext.Provider value={layout}>
    <div
      data-slot="message-cards"
      data-layout={layout}
      className={cn(layoutClasses[layout], className)}
      {...props}
    />
  </MessageCardsLayoutContext.Provider>
);

MessageCards.displayName = "MessageCards";

// Snap classes used when the parent collection lays out horizontally.
// We mirror the layoutClasses three modes so the card knows when to
// shrink-not-grow + snap. (Stack mode: no snap, full-width.)
const cardLayoutClasses: Record<MessageCardsLayout, string> = {
  stack: "w-full",
  carousel: "w-64 flex-none snap-start",
  responsive: "w-full md:w-64 md:flex-none md:snap-start",
};

export type MessageCardProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "onSelect"
> & {
  /**
   * Click handler for the whole card. When provided, the card becomes
   * a button-roled, keyboard-activatable surface.
   */
  onSelect?: () => void;
  /**
   * Override the layout this card sizes for. Inherited from the
   * surrounding `MessageCards` collection via context; explicit prop
   * wins. Defaults to `stack` when used standalone (no collection).
   */
  layout?: MessageCardsLayout;
};

export const MessageCard = ({
  onSelect,
  layout,
  className,
  ...props
}: MessageCardProps) => {
  const inheritedLayout = useContext(MessageCardsLayoutContext);
  const effectiveLayout = layout ?? inheritedLayout ?? "stack";
  const interactive = typeof onSelect === "function";
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect?.();
    }
  };

  return (
    <div
      data-slot="message-card"
      className={cn(
        cardChrome,
        cardLayoutClasses[effectiveLayout],
        interactive &&
          "cursor-pointer text-left transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring",
        className,
      )}
      onClick={interactive ? onSelect : undefined}
      onKeyDown={handleKeyDown}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      {...props}
    />
  );
};

MessageCard.displayName = "MessageCard";

export type MessageCardMediaProps = ComponentProps<"img"> & {
  /** Aspect ratio for the media box. Defaults to 16/9. */
  aspect?: "square" | "video" | "wide";
};

const aspectClasses: Record<
  NonNullable<MessageCardMediaProps["aspect"]>,
  string
> = {
  square: "aspect-square",
  video: "aspect-video",
  wide: "aspect-[16/7]",
};

export const MessageCardMedia = ({
  aspect,
  className,
  alt,
  ...props
}: MessageCardMediaProps) => (
  <img
    alt={alt ?? ""}
    className={cn(
      "w-full object-cover",
      aspect ? aspectClasses[aspect] : "h-30",
      className,
    )}
    data-slot="message-card-media"
    {...props}
  />
);

MessageCardMedia.displayName = "MessageCardMedia";

export type MessageCardBodyProps = ComponentProps<"div">;

export const MessageCardBody = ({
  className,
  ...props
}: MessageCardBodyProps) => (
  <div
    data-slot="message-card-body"
    className={cn("flex flex-col gap-1 p-3", className)}
    {...props}
  />
);

MessageCardBody.displayName = "MessageCardBody";

export type MessageCardTitleProps = ComponentProps<"div">;

export const MessageCardTitle = ({
  className,
  ...props
}: MessageCardTitleProps) => (
  <div
    data-slot="message-card-title"
    className={cn("font-medium text-card-foreground text-sm", className)}
    {...props}
  />
);

MessageCardTitle.displayName = "MessageCardTitle";

export type MessageCardDescriptionProps = ComponentProps<"div">;

export const MessageCardDescription = ({
  className,
  ...props
}: MessageCardDescriptionProps) => (
  <div
    data-slot="message-card-description"
    className={cn("font-normal text-muted-foreground text-sm", className)}
    {...props}
  />
);

MessageCardDescription.displayName = "MessageCardDescription";

export type MessageCardActionsProps = ComponentProps<"div">;

export const MessageCardActions = ({
  className,
  ...props
}: MessageCardActionsProps) => (
  <div
    data-slot="message-card-actions"
    className={cn("flex flex-wrap items-center gap-2 px-3 pb-3", className)}
    {...props}
  />
);

MessageCardActions.displayName = "MessageCardActions";

// Convenience: a navigate-style card (anchor wrapper). Useful for the
// common "tap the card to open this URL" case without consumers wiring
// up a button-roled div + onSelect.
export type MessageCardLinkProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "children"
> & {
  layout?: MessageCardsLayout;
  children?: ReactNode;
};

export const MessageCardLink = ({
  layout,
  className,
  children,
  ...props
}: MessageCardLinkProps) => {
  const inheritedLayout = useContext(MessageCardsLayoutContext);
  const effectiveLayout = layout ?? inheritedLayout ?? "stack";

  return (
    // Focus styles live on the <a> itself — that's what receives focus.
    // Putting them on the inner card meant :focus-visible never matched
    // because the inner div was not in the tab order.
    <a
      data-slot="message-card-link"
      className={cn(
        "block rounded-lg no-underline focus-visible:outline-2 focus-visible:outline-ring",
        cardLayoutClasses[effectiveLayout],
        className,
      )}
      {...props}
    >
      <div
        data-slot="message-card"
        className={cn(cardChrome, "transition-colors hover:bg-accent")}
      >
        {children}
      </div>
    </a>
  );
};

MessageCardLink.displayName = "MessageCardLink";
